import { generatePagerProps } from '@fukuri-admins/utils/common/generatePagerProps';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { DeleteClientDialog } from './features/DeleteClientDialog';
import { useClientList } from './hooks';
import { convertToPresentationProps } from './logics';
import { ClientListPresentation } from './presentations';
import { ClientListEmptyPresentation } from './presentations/empty';

/**
 * 利用企業一覧 Container
 *
 * - 状態管理・アクションは useClientList に委譲する
 * - API 型 → Presentation 型の変換は convertToPresentationProps に委譲する
 * - Empty / 通常表示の出し分けは isEmpty フラグで行う（三項演算子）
 * - 検索条件あり × 0 件のときは Empty を表示せず検索フォーム＋テーブルを表示する（PEN-309 対応）
 * - カスタムフィールド定義に基づいてテーブル列を動的に挿入する（PEN-563 T04）
 * - creditEvaluationEnabled=true のとき与信状況列・未与信件数アラート・与信確認モーダルを表示する（PEN-564 T05）
 * - 利用企業削除ダイアログを Overlay パターンで並列配置する（PEN-566 T07a）
 * - T08（PEN-699）: 削除成功後の一覧再フェッチ・未与信カウント再取得
 * - threeLetterCode は DeleteClientDialog Feature 内部で Cookie から取得するため、ここでは渡さない
 * - isStagingFeatureEnabled（= getEnvFeatureFlag().enableStaging）をここで1回だけ判定し、
 *   ClientsTable の取引店舗列・DeleteClientDialog の取引店舗表示・convertToPresentationProps の
 *   カスタムフィールド「取引店舗」除外判定の3箇所に同じ値を渡す。
 *   「取引店舗」はお申込みフォームPJ（別PJ）の管轄になったため、一旦 enableStaging で本番非表示にしている。
 *   単一の Container をフラグソースにすることで出し分けが必ず一致する。
 * - convertToPresentationProps へ isStagingFeatureEnabled を渡すことで、staging では固定列
 *   PartnerBranch とカスタムフィールド「取引店舗」の二重表示を防ぎ、production/demo では
 *   固定列が未表示のためカスタム列「取引店舗」をそのまま表示する（PEN-988）
 * - hasPartnerBranch（マスタ取引店舗一覧 API に1件以上存在するか）を
 *   ClientsTable の取引店舗列・DeleteClientDialog の取引店舗表示に isStagingFeatureEnabled との
 *   AND 条件として渡す（PEN-985）。パートナー配下に取引店舗が1件も無い場合は staging でも
 *   固定列を表示しない。raw データ（partnerBranchesData）は useClientList から受け取り、
 *   hasPartnerBranch への導出は convertToPresentationProps の責務とする（計算はconverterで行う）
 *
 * NOTE: 親コンポーネント（Screen）で Suspense にラップすること
 * NOTE: PEN-569（利用企業一覧 新パターン刷新）の後続チケットで段階的に拡充する
 */
export const ClientList: React.FC = () => {
  const { enableStaging: isStagingFeatureEnabled } = getEnvFeatureFlag();

  const {
    state: {
      clientsData,
      totalPage,
      currentPage,
      totalCount,
      nameValue,
      creditGrantedValue,
      isFiltered,
      featureFlagsData,
      customFieldDefinitionsData,
      ungrantedCreditCount,
      isDownloadingCsv,
      deleteTargetClient,
      partnerBranchesData,
    },
    actions: {
      handlePageChange,
      handleEditClient,
      handleDeleteClient,
      handleCloseDeleteClientDialog,
      handleDeleteClientSuccess,
      handleRowClick,
      handleChangeName,
      handleChangeCreditGranted,
      handleSearch,
      handleReset,
      handleDownloadCsv,
      handleGrantCredit,
    },
  } = useClientList();

  const { rows, customFieldColumns, isEmpty, creditEvaluationEnabled, hasPartnerBranch } =
    convertToPresentationProps({
      clientsData,
      isFiltered,
      featureFlagsData,
      customFieldDefinitionsData,
      ungrantedCreditCount,
      isStagingFeatureEnabled,
      partnerBranchesData,
    });

  const pagerProps = generatePagerProps({
    currentPage,
    totalPage,
    totalCount,
    rows,
    onPageChange: handlePageChange,
  });

  return (
    <>
      {isEmpty ? (
        <ClientListEmptyPresentation />
      ) : (
        <ClientListPresentation
          rows={rows}
          customFieldColumns={customFieldColumns}
          pagerProps={pagerProps}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onRowClick={handleRowClick}
          nameValue={nameValue}
          onChangeName={handleChangeName}
          creditGrantedValue={creditGrantedValue}
          onChangeCreditGranted={handleChangeCreditGranted}
          creditEvaluationEnabled={creditEvaluationEnabled}
          isStagingFeatureEnabled={isStagingFeatureEnabled}
          hasPartnerBranch={hasPartnerBranch}
          ungrantedCreditCount={ungrantedCreditCount}
          onGrantCredit={handleGrantCredit}
          onSearch={handleSearch}
          onReset={handleReset}
          onDownloadCsv={handleDownloadCsv}
          isDownloadingCsv={isDownloadingCsv}
        />
      )}
      {deleteTargetClient && (
        <DeleteClientDialog
          name={deleteTargetClient.name}
          partnerBranchName={deleteTargetClient.partnerBranchName}
          contactPersonName={deleteTargetClient.contactPersonName}
          contractPlanName={deleteTargetClient.contractPlanName}
          userCount={deleteTargetClient.userCount}
          clientId={String(deleteTargetClient.id)}
          isStagingFeatureEnabled={isStagingFeatureEnabled}
          hasPartnerBranch={hasPartnerBranch}
          onDelete={handleDeleteClientSuccess}
          onClose={handleCloseDeleteClientDialog}
        />
      )}
    </>
  );
};
